import styled, { DefaultTheme } from "styled-components";
import { Card, Typography, Button, Space } from "antd";

const { Title, Text } = Typography;

// Declare module augmentation for styled-components theme
declare module "styled-components" {
  export interface DefaultTheme {
    mode?: "light" | "dark";
  }
}

// Theme helper function
const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === "dark" ? darkValue : lightValue;
};

export const MainContainer = styled.div`
  padding: 32px;
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  background: ${(props) => getThemeValue(props, "#1A202C", "#ffffff")} !important;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

export const BackButton = styled(Button)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
`;

export const FullWidthSpace = styled(Space)`
  width: 100%;
`;

export const FullWidthVerticalSpace = styled(Space)`
  width: 100%;
`;

export const PageTitle = styled(Title)`
  margin: 0 !important;
`;

export const InheritText = styled(Text)`
  color: inherit !important;
`;

export const StyledText = styled(Text)<{ color?: string }>`
  color: ${(props) => {
    if (props.color) return props.color;
    return getThemeValue(props, "#CBD5E0", "#718096");
  }} !important;
  font-weight: 400;
`;

export const SecondaryText = styled(StyledText)`
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

// InfoTile Styles
export const InfoTileCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border-radius: 16px;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  height: 100%;
  cursor: pointer;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 50%, #fa8c16 100%);
    opacity: 0.8;
    z-index: 1;
  }

  &:hover {
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 30px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
      "0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)"
    )};
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 32px;
    background: transparent !important;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const InfoTileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
`;

export const InfoTileContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const InfoTileIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const InfoTileIcon = styled.span`
  font-size: 24px;
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")};
`;

export const InfoTileTitle = styled(Title)`
  margin: 0 !important;
  font-size: 20px !important;
  font-weight: 600 !important;
`;

export const InfoTileSubtitle = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const InfoTileValue = styled(Title)`
  margin: 0 !important;
  font-size: 32px !important;
  font-weight: 700 !important;
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
`;

// ItemsRankingGrid Styles
export const RankingGridCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border-radius: 16px;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  height: 100%;

  .ant-card-body {
    padding: 24px;
    background: transparent !important;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const RankingGridHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: center;
  background: ${(props) => getThemeValue(props, "#4A5568", "#F7FAFC")};
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

export const RankingGridRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: center;
  padding: 12px 0;
  border-bottom: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};

  &:last-child {
    border-bottom: none;
  }
`;

export const RankingGridHeaderText = styled(Text)`
  font-weight: 600 !important;
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
`;

export const RankingGridItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const RankingGridItemImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: ${(props) => getThemeValue(props, "2px solid #4A5568", "2px solid #E2E8F0")};
`;

export const RankingGridText = styled(Text)`
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  text-align: right;
`;

// Chart Container Styles
export const ChartContainer = styled.div`
  border-radius: 16px;
  min-height: 450px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  padding: 0;
  margin: 0;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 50%, #fa8c16 100%);
    opacity: 0.6;
    z-index: 1;
  }

  > div:not([class*="ant-dropdown"]) {
    background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
    max-width: 100%;
    overflow-x: auto;
  }
  
  .ant-dropdown,
  .ant-picker-dropdown,
  [class*="ant-select-dropdown"] {
    z-index: 1050 !important;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const ChartTitle = styled(Title)`
  margin: 20px 24px 0 24px !important;
  font-size: 20px !important;
  font-weight: 600 !important;
`;

export const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 24px;
  width: calc(100% - 32px);
  max-width: 100%;
  box-sizing: border-box;
  justify-content: flex-start;
  background: ${(props) => getThemeValue(props, "#4A5568", "#F7FAFC")};
  border-radius: 12px;
  margin: 16px;
  position: relative;
  z-index: 2;
  overflow: visible;

  > * {
    position: relative;
    z-index: 10;
  }

  @media (max-width: 768px) {
    padding: 16px;
    gap: 12px;
    width: calc(100% - 24px);
    margin: 12px;
  }
`;

export const MetricsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 20px 24px;
  width: calc(100% - 32px);
  max-width: 100%;
  box-sizing: border-box;
  margin: 0 16px;
  justify-content: center;
  align-items: center;
  background: ${(props) => getThemeValue(props, "#4A5568", "#F7FAFC")};
  border-radius: 12px;

  @media (max-width: 768px) {
    gap: 16px;
    padding: 16px;
    width: calc(100% - 24px);
    margin: 0 12px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const MetricBadge = styled(Space)<{ color: string }>`
  padding: 12px 20px;
  border-radius: 10px;
  background: ${(props) => {
    const colorMap: { [key: string]: string } = {
      "#52c41a": "rgba(82, 196, 26, 0.1)",
      "#1890ff": "rgba(24, 144, 255, 0.1)",
      "#fa8c16": "rgba(250, 140, 22, 0.1)",
      "#722ed1": "rgba(114, 46, 209, 0.1)",
    };
    return colorMap[props.color] || "rgba(82, 196, 26, 0.1)";
  }};
  border: ${(props) => {
    const colorMap: { [key: string]: string } = {
      "#52c41a": "1px solid rgba(82, 196, 26, 0.2)",
      "#1890ff": "1px solid rgba(24, 144, 255, 0.2)",
      "#fa8c16": "1px solid rgba(250, 140, 22, 0.2)",
      "#722ed1": "1px solid rgba(114, 46, 209, 0.2)",
    };
    return colorMap[props.color] || "1px solid rgba(82, 196, 26, 0.2)";
  }};
  transition: all 0.3s ease;
`;

export const MetricIcon = styled.span<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 18px;
`;

export const MetricLabel = styled(Text)`
  font-weight: 500;
  color: inherit !important;
`;

export const MetricValue = styled(Text)<{ color: string }>`
  font-size: 16px;
  color: ${(props) => props.color} !important;
`;

export const ChartWrapper = styled.div`
  width: calc(100% - 32px);
  max-width: 100%;
  height: 380px;
  padding: 16px;
  margin: 0 16px 16px 16px;
  background-color: transparent;
  position: relative;
  z-index: 1;
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;
`;

export const ChartSpaceWrapper = styled(Space)`
  width: 100%;
  max-width: 100%;
  padding: 0;
  box-sizing: border-box;
  overflow: visible;
`;

// Table Styles
export const TableContainer = styled(Card)`
  margin-top: 32px;
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border-radius: 16px;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  overflow: hidden;

  .ant-card-body {
    padding: 24px;
    background: transparent !important;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }

  /* Fix table header styling in dark mode */
  .ant-table-thead > tr > th {
    border-bottom: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")} !important;
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
    
    /* Sort indicator styling */
    .ant-table-column-sorter {
      color: ${(props) => getThemeValue(props, "#ffffff", "#bfbfbf")} !important;
      
      .ant-table-column-sorter-inner {
        .ant-table-column-sorter-up,
        .ant-table-column-sorter-down {
          color: ${(props) => getThemeValue(props, "#ffffff", "#bfbfbf")} !important;
          
          &.on {
            color: ${(props) => getThemeValue(props, "#1890ff", "#1890ff")} !important;
          }
        }
      }
    }
    
    /* Sort icon when active */
    .ant-table-column-sort {
      .ant-table-column-sorter-inner {
        .ant-table-column-sorter-up.on,
        .ant-table-column-sorter-down.on {
          color: ${(props) => getThemeValue(props, "#1890ff", "#1890ff")} !important;
        }
      }
    }
  }

  /* Ensure pagination is visible in dark mode */
  .ant-pagination {
    .ant-pagination-item {
      a {
        color: ${(props) => getThemeValue(props, "#ffffff", "#4e5156")} !important;
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      .ant-pagination-item-link {
        color: ${(props) => getThemeValue(props, "#ffffff", "#444")} !important;
        
        svg {
          color: ${(props) => getThemeValue(props, "#ffffff", "#444")} !important;
          fill: ${(props) => getThemeValue(props, "#ffffff", "#444")} !important;
        }
      }
    }
  }

  /* Table body text styling */
  .ant-table-tbody {
    tr {
      td {
        color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
        
        p {
          color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
        }
        
        span {
          color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
        }
      }
    }
  }

  /* Empty state text styling */
  .ant-empty {
    .ant-empty-description {
      color: ${(props) => getThemeValue(props, "#ffffff", "#8c8c8c")} !important;
    }
    
    .ant-empty-image {
      svg {
        fill: ${(props) => getThemeValue(props, "#4A5568", "#d9d9d9")} !important;
      }
    }
  }

  /* Table placeholder text */
  .ant-table-placeholder {
    .ant-empty {
      .ant-empty-description {
        color: ${(props) => getThemeValue(props, "#ffffff", "#8c8c8c")} !important;
      }
    }
  }
`;

export const TableTitle = styled(Title)`
  margin-bottom: 20px !important;
  font-size: 20px !important;
  font-weight: 600 !important;
`;

export const TableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  overflow-x: auto;
`;

export const TableHeaderCell = styled.div<{ onClick?: () => void }>`
  background: ${(props) => getThemeValue(props, "#4A5568", "#F7FAFC")};
  padding: 12px 16px;
  border-radius: 8px;
  cursor: ${(props) => (props.onClick ? "pointer" : "default")};
  transition: all 0.3s ease;
  user-select: none;

  &:hover {
    background: ${(props) => getThemeValue(props, "#5A6578", "#EDF2F7")};
  }
`;

export const TableHeaderText = styled(Text)`
  display: flex;
  font-weight: 600 !important;
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  justify-content: space-between;
  align-items: center;
`;

export const TableRowCell = styled.div`
  padding: 12px 16px;
  border-bottom: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  display: flex;
  align-items: center;
`;

export const TableRowText = styled(Text)`
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  text-align: right;
  width: 100%;
`;

export const TableItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const TableItemImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: ${(props) => getThemeValue(props, "2px solid #4A5568", "2px solid #E2E8F0")};
`;

export const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

// Summary Section Styles
export const SummarySection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const SummaryItem = styled.div`
  flex: 1;
  min-height: 200px;
`;
